// Small score widgets: FED/CDI/LRM trio, legend, per-shift bars, comparison bar.

import React from 'react';
import { cn } from '@/lib/utils';
import { ScoreChip, fedTone, toneBg, toneText, toneVar } from './ui';
import type { Tone } from './ui';
import { cdiTone, lrmTone, pliTone } from './engine/scores';
import type { Worker } from './engine/data';

export const ScoreTrio: React.FC<{ w: Worker }> = ({ w }) => (
  <div className='flex items-center gap-1.5'>
    <ScoreChip label='FED' value={w.fed} color={toneVar[fedTone(w.fed)]} />
    <ScoreChip label='CDI' value={w.cdi} color={toneVar[cdiTone(w.cdi)]} />
    <ScoreChip label='LRM' value={w.lrm > 0 ? `+${w.lrm}` : w.lrm} color={toneVar[lrmTone(w.lrm)]} />
  </div>
);

const LEGEND: { dot: string; abbr: string; text: string }[] = [
  { dot: 'bg-info', abbr: 'FED', text: 'Fatigue Exposure Dose — biometric state (HRV 45% · RHR 30% · Sleep 25%)' },
  { dot: 'bg-violet', abbr: 'CDI', text: 'Circadian Disruption Index — shift effect on body clock' },
  { dot: 'bg-warning', abbr: 'LRM', text: 'Load Readiness Mismatch — state vs shift demand (+ve = under-ready)' },
];

export const ScoreLegend: React.FC = () => (
  <div className='flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground'>
    {LEGEND.map((l) => (
      <div key={l.abbr} className='flex items-center gap-1.5'>
        <span className={cn('h-2 w-2 shrink-0 rounded-full', l.dot)} />
        <strong className='text-foreground'>{l.abbr}</strong> {l.text}
      </div>
    ))}
  </div>
);

const ShiftBar: React.FC<{ score: number; tone: Tone }> = ({ score, tone }) => (
  <div className='flex items-center gap-1.5'>
    <div className='h-1.5 w-[52px] overflow-hidden rounded-full bg-border'>
      <div className={cn('h-full rounded-full', toneBg[tone])} style={{ width: `${Math.min(100, score)}%` }} />
    </div>
    <span className={cn('min-w-[22px] text-[11px] font-semibold', toneText[tone])}>{score}</span>
  </div>
);

export const ShiftCdiBar: React.FC<{ score: number }> = ({ score }) => <ShiftBar score={score} tone={cdiTone(score)} />;

export const ShiftPliBar: React.FC<{ score: number }> = ({ score }) => <ShiftBar score={score} tone={pliTone(score)} />;

/** Value bar scaled to `max`; `color` is any CSS colour (e.g. toneVar[...]). */
export const CompBar: React.FC<{ val: number; max: number; color: string }> = ({ val, max, color }) => {
  const pct = Math.min(100, Math.round((val / max) * 100));
  return (
    <div className='flex items-center gap-2'>
      <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-muted'>
        <div className='h-full rounded-full' style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className='min-w-[26px] text-right text-xs font-semibold' style={{ color }}>
        {val}
      </span>
    </div>
  );
};
